import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Progress } from "antd";
import requests from "../../../../../axios/requests";
import Button from "../../../../../components/Button/Button";
import { setCollapse } from "../../../../../redux/actions/actions";
import LZString from "../../../../../../utils/LZString";
import Comments from "../Comments/Comments";
import "./Scorm.scss";

// cmi.suspend_data is an LZString-compressed JSON blob whose inner shape is
// specific to whichever tool authored the SCORM package (see
// project_education_scorm_progress_ui memory) - only used to derive a live
// "slides viewed so far" percent for the progress ring, ported from Staff's
// working Scorm.jsx (LessonNew.jsx/Lesson.jsx render an equivalent ring).
// Returns "unknown" (caller then hides the ring) for any package whose
// suspend_data doesn't match either recognized shape.
const detectSuspendDataFormat = (scormData) => {
  const vLength = scormData?.V?.length || 0;
  const slideStatesCount = scormData?.p?.slideStates
    ? Object.keys(scormData.p.slideStates).length
    : 0;
  if (Array.isArray(scormData?.V) && vLength > 0) {
    const maxVIndex = Math.max(...scormData.V);
    const hasGaps = vLength < maxVIndex + 1;
    return hasGaps || vLength > slideStatesCount + 1 ? "old" : "new";
  }
  return "unknown";
};

const computeSlideProgress = (scormData) => {
  const format = detectSuspendDataFormat(scormData);
  let total = 1;
  let current = 0;

  if (format === "new") {
    let quizFound = false;
    let quizTotal = 0;
    let quizCurrent = 0;
    for (const key in scormData.p?.slideStates || {}) {
      const quizState = scormData.p.slideStates[key]?.quizState?.S;
      if (quizState?.sp) {
        quizFound = true;
        quizTotal = quizState.sp.q?.length || 0;
        quizCurrent = quizState.sp.c >= 0 ? quizState.sp.c : -1;
      }
    }
    if (quizFound && quizTotal > 0) {
      total = quizTotal;
      current = quizCurrent + 1;
    } else {
      const vLength = scormData?.V?.length || 0;
      const slideCount = Object.keys(scormData.p?.slideStates || {}).length;
      total = Math.max(vLength, slideCount, 1);
      current = (scormData.p?.lastViewedSlide ?? 0) + 1;
    }
  } else if (format === "old") {
    const maxV =
      Array.isArray(scormData.V) && scormData.V.length
        ? Math.max(...scormData.V)
        : 0;
    total = Math.max(scormData.V?.length || 0, maxV + 1);
    current = (scormData.l ?? 0) + 1;
  } else {
    return null;
  }
  return Math.min(100, Math.round((current / total) * 100));
};

const Scorm = ({ taskProps, setTriggerLmsEnd, fullScreen, setFullScreen }) => {
  const [timeoutLms, setTimeoutLms] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [comments, setComments] = useState("");
  const [valueComment, setValueComment] = useState("");
  const [slideProgress, setSlideProgress] = useState(null);
  const dispatch = useDispatch();
  const { collapse } = useSelector((state) => state.Reducer);
  console.log("collapse", collapse);

  let refInteractionsCount = useRef(0);
  let refData = useRef(null);

  let splitUrl = () => {
    let oldUrl = process.env.REACT_APP_API_URL.split("skillometer");
    return `${oldUrl[0]}web.skillometer${oldUrl[1]}`;
  };
  let sendApiLms = (data) => {
    requests.stages
      .lms_data(data, taskProps?.id)
      .then(() => {
        // SCORM 1.2 spec: content with a graded assessment reports "passed"/"failed",
        // content without one reports "completed"/"incomplete" -- "browsed" also counts as
        // done (matches backend LMSPackageProgress.SUCCESS_STATUSES in apps/quests/models).
        // AI-generated SCORM (dark_it and all sibling themes) always reports "passed" here,
        // so a literal === "completed" check never fired for any AI content.
        const successStatuses = ["passed", "completed", "browsed"];
        if (
          data &&
          typeof data?.raw_data !== "undefined" &&
          typeof data.raw_data["cmi.core.lesson_status"] !== "undefined" &&
          successStatuses.includes(data.raw_data["cmi.core.lesson_status"])
        ) {
          setTriggerLmsEnd((prev) => prev + 1);
          clearTimeout(timeoutLms);
        }
      })
      .catch((err) => console.error(err));
  };
  let getApiComments = () => {
    requests.comments
      .get_comment_matherial({
        contentType: 46,
        objectId: taskProps.id,
      })
      .then((res) => setComments(res.data))
      .catch((error) => console.log(error, "error get_comments"));
  };
  let sendComment = () => {
    if (!valueComment) return;

    let obj = {
      user: {
        profile: {
          // eslint-disable-next-line no-undef
          avatar: user.avatar,
        },
      },
      content_type: 46,
      object_id: taskProps.id,
      text: valueComment,
    };
    requests.comments
      .add_comment_matherial(obj)
      .then((res) => setComments((prev) => [...prev, res.data]))
      .catch((error) => console.log(error, "error add_comment"));
    setValueComment("");
  };
  let handleSetLikeDislake = (id, value) => {
    let obj = {
      content_type: 86,
      object_id: id,
      value: value,
    };
    requests.comments
      .set_like(obj)
      .then(() => getApiComments())
      .catch((error) => console.error(error));
  };

  window.API = {
    LMSInitialize() {
      requests.stages
        .lmg_initialize(taskProps?.id)
        .catch((err) => console.error(err));
      return true;
    },
    LMSFinish() {
      clearTimeout(timeoutLms);
      refData.current = null;
      requests.stages
        .lmg_finish(taskProps?.id)
        .then(() => {
          clearTimeout(timeoutLms);
          refData.current = null;
        })
        .catch((err) => console.error(err));
      return true;
    },
    LMSGetValue(value) {
      if (value === "cmi.interactions._count") {
        let prevIterationCount = JSON.parse(
          JSON.stringify(refInteractionsCount)
        );
        refInteractionsCount.current = prevIterationCount.current + 1;
        return prevIterationCount.current;
      }
      return "";
    },
    LMSSetValue(value, value_2) {
      let obj = {};
      if (refData?.current) {
        obj = { ...refData?.current?.raw_data };
        obj[value] = value_2;
      } else {
        obj = { [value]: value_2 };
      }
      let newObj = {
        raw_data: obj,
      };
      refData.current = newObj;
      setResultData(newObj);
      if (value === "cmi.suspend_data") {
        try {
          const scormData = LZString.decompressFromBase64(value_2);
          const percent = scormData ? computeSlideProgress(scormData) : null;
          if (percent !== null) setSlideProgress(percent);
        } catch (e) {
          // Different authoring tool, different (unparseable) suspend_data shape -
          // no live ring for this package, not an error worth surfacing.
        }
      }
    },
    LMSCommit() {
      return true;
    },
    LMSGetLastError() {
      return "0";
    },
    LMSGetErrorString() {
      return "";
    },
    LMSGetDiagnostic() {
      return "";
    },
  };

  useEffect(() => {
    getApiComments();
    clearTimeout(timeoutLms);
    refData.current = null;
    return () => {
      clearTimeout(timeoutLms);
      refData.current = null;
    };
  }, [taskProps?.id]);
  useEffect(() => {}, [window.API.LMSSetValue]);
  useEffect(() => {
    if (refData?.current && !taskProps?.seen) {
      clearTimeout(timeoutLms);
      setTimeoutLms(
        setTimeout(
          (data) => {
            sendApiLms(data);
            setLastRequest(data);
          },
          1000,
          refData.current
        )
      );
    }
  }, [refData.current]);

  const iframe = document.getElementById("myIframe");
  try {
    // Доступ к документу внутри iframe
    const iframeDocument =
      iframe.contentDocument || iframe.contentWindow.document;

    // Получаем элемент body внутри iframe
    const body = iframeDocument.body;

    // Меняем стиль position на static
    body.style.position = "static";
  } catch (error) {
    console.log("Не удалось получить доступ к содержимому iframe:", error);
  }
  return (
    <div className={fullScreen ? "scorm fullscreen" : "scorm"}>
      {taskProps.title && (
        <div className="scorm_title">
          <div className="title">{taskProps.title}</div>
          {(taskProps?.seen || slideProgress !== null) && (
            <Progress
              type="dashboard"
              percent={taskProps?.seen ? 100 : slideProgress}
              gapDegree={1}
              strokeColor={
                (taskProps?.seen ? 100 : slideProgress) >= 100
                  ? "#30E0A1"
                  : "#FFD23B"
              }
              width={35}
              height={35}
              format={(p) => Math.round(p)}
              trailColor="#474C5A"
              strokeWidth={20}
              className="progress_circle"
            />
          )}
        </div>
      )}
      <iframe
        // Same-origin as this page (matches front_edu's working teacher-preview embed) --
        // a cross-origin iframe here previously blocked Fullscreen (browsers gate
        // requestFullscreen() behind a Permissions Policy for cross-origin frames without
        // an explicit `allow`) and the contentDocument access below (silently caught, so
        // the SCORM shell's `position: fixed` layout was never patched for embedding).
        src={
          window.location.origin +
          `/api/v2/web/student/stage/stage_theory/${taskProps?.id}/lms/main`
        }
        width={"100%"}
        id={"myIframe"}
        key={taskProps?.id}
        allow="fullscreen"
      />
      {fullScreen !== undefined && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            margin: "15px 0",
          }}
        >
          <Button
            width={"150px"}
            className={"fullscreen_btn"}
            type="submit"
            text={fullScreen ? "Уменьшить" : "Увеличить"}
            icon={{
              position: "left",
              name: fullScreen ? "resizeMin" : "resizeMax",
            }}
            click={() => {
              setFullScreen(!fullScreen);
              dispatch(setCollapse(!collapse));
            }}
          />
        </div>
      )}
      {window.innerWidth > 820 && (
        <Comments
          comments={comments}
          sendComment={sendComment}
          handleSetLikeDislake={handleSetLikeDislake}
          valueComment={valueComment}
          setValueComment={setValueComment}
        />
      )}
    </div>
  );
};

export default Scorm;
